import { CRAYO_ENTRY, crayoPlan, formatHours, money, crayoPlanTerms } from "@/lib/compare/crayo";
import type { CompareContent } from "@/lib/compare/types";

const hobby = CRAYO_ENTRY;
const clipper = crayoPlan("Clipper");

export const SUBMAGIC_CONTENT: CompareContent = {
  slug: "crayo-vs-submagic",
  competitorSlug: "submagic",
  title: "Crayo vs Submagic",
  seoTitle: "Crayo vs Submagic: Pricing, Video Caps and Features Compared",
  seoDescription:
    "Crayo vs Submagic, with dated prices. Submagic caps credits, video count and minutes per video. Crayo charges 1 credit per finished clip, no per-video cap.",
  cardVerdict: "Better than Crayo for caption styling and scheduling, behind on video caps and clipping.",
  bestFor: "Clippers hitting Submagic's per-video caps",
  datePublished: "2026-10-09",
  dateModified: "2026-10-25",
  verdict:
    "As of October 25, 2026, Crayo is the better pick if you clip podcasts, streams or interviews longer than 2 or 5 minutes, want one credit per finished clip instead of three stacked caps, and want a timeline editor with 32 caption styles on the entry plan. Submagic is the better pick if animated caption styling, a scheduler, AI actors, dubbing or 4K export decide it. The evidence is below.",
  pickCrayoIf: [
    "Your sources run past 2 minutes: Submagic Starter caps each video at 2 minutes and Pro at 5, while Crayo AutoClip takes podcasts and stream VODs up to 3 hours and the editor is metered by export minutes.",
    `You want one number to watch: Crayo Hobby is ${hobby.credits} credits, one per finished clip, for ${money(hobby.monthly)} a month, and Submagic Starter is 45 credits, 15 videos and a 2-minute cap for $19.`,
    "You want the clip reframed, not only captioned: AutoClip's face-aware camera track follows the speaker, and the timeline editor with camera, keyframes, speed ramps, 32 caption styles and 56 spoken languages is on the entry plan.",
  ],
  pickThemIf: [
    "Animated caption styling is the whole job: Submagic is built around it and claims 123 caption languages to Crayo's 56.",
    "You schedule posts from the editing tool: Submagic has a social media scheduler, Crayo has none.",
    "You need AI actors, AI dubbing, 4K at 60fps (Business) or team seats. Crayo has none of these.",
  ],
  wins: [
    {
      claim: "Crayo has no per-video minute cap on the entry plan; Submagic caps each video at 2, 5 or 30 minutes by plan.",
      evidence:
        "Submagic's pricing page, checked Oct 8, 2026, lists a 2-minute cap per video on Starter, 5 minutes on Pro and 30 minutes on Business. Crayo AutoClip accepts sources from 1 minute to 3 hours or 4 GB on every plan, and editor exports draw on " +
        `${formatHours(hobby.exportMinutes)} of export minutes a month on Hobby.`,
      href: "https://www.submagic.co/pricing",
    },
    {
      claim: "Crayo meters one credit per finished clip; Submagic stacks three limits at once.",
      evidence:
        `Submagic Starter is 45 credits, which its page counts as 15 videos, so a video costs 3 credits and the video count and the minute cap both apply on top. Crayo Hobby is ${hobby.credits} workflow credits for ${money(hobby.monthly)} a month, one credit per AutoClip clip or AI edit, and Find Viral Moments and Clip Cutter spend nothing until you cut.`,
      href: "https://www.submagic.co/pricing",
    },
    {
      claim: "Crayo AutoClip reframes with a face-aware camera; Submagic's pages do not describe face tracking.",
      evidence:
        "Submagic's Magic Clips page, checked Oct 8, 2026, promises 20+ clips from one upload but does not describe speaker tracking; only a third-party comparison table credits it with reframing. Crayo's camera track follows the speaker on every clip, with speaker, streamer, split and full layouts.",
      href: "https://www.submagic.co/features/magic-clips",
    },
    {
      claim: "Crayo's timeline editor is on every plan; Submagic describes its editing as text-based cut and trim.",
      evidence:
        "Submagic's own comparison pages say it is not built for advanced or cinematic edits. Crayo's editor has a camera track, keyframes, zooms, transitions, speed ramps, motion blur, 32 caption styles and keyword highlight on Hobby.",
      href: "https://crayo.ai/features/auto-clip",
    },
    {
      claim: "Crayo includes View Tracker on every plan; Submagic lists no creator tracking.",
      evidence:
        `Submagic's pages list image and GIF inserts, not generation, and AI dubbing rather than text-to-speech, as of Oct 8, 2026, and no creator tracking. Crayo Hobby includes ${hobby.trackerCredits.toLocaleString("en-US")} View Tracker credits a month, with posting cadence and viral outliers for Instagram, YouTube and TikTok creators, and the 34-endpoint API on the same plan.`,
      href: "https://www.submagic.co",
    },
  ],
  concessions: [
    {
      claim: "Submagic's animated captions are its core product and it claims more caption languages.",
      evidence:
        "The Submagic homepage claims 123 caption languages (its vs pages say 100+), with animated templates and emoji placement. Crayo transcribes 56 spoken languages and ships 32 caption styles.",
      href: "https://www.submagic.co",
    },
    {
      claim: "Submagic schedules and publishes posts; Crayo does not.",
      evidence: "Submagic has a social media scheduler page as of Oct 8, 2026. Crayo exports the file and you post it yourself.",
      href: "https://www.submagic.co/social-media-scheduler",
    },
    {
      claim: "Submagic offers AI actors, AI dubbing, 4K at 60fps and team seats.",
      evidence:
        "Business includes 4K at 60fps export and unlimited seats, Pro includes 3 seats, and the homepage lists AI actors and dubbing. Crayo exports at 1080p, has no avatars, no dubbing and no team seats.",
      href: "https://www.submagic.co/pricing",
    },
    {
      claim: "Submagic has a free account and a far bigger footprint.",
      evidence:
        "Submagic's own comparison pages offer 3 free videos with no credit card, and the homepage claims 4M+ businesses. Crayo has no free plan and no trial.",
      href: "https://www.submagic.co/vs/veed-io-vs-submagic",
    },
  ],
  typicalMonth: {
    scenario: "One 60-minute source a week, eight clips each: 240 source minutes and 32 clips a month.",
    crayo: {
      plan: "Hobby",
      cost: `${money(hobby.monthly)}/mo or about ${money(hobby.yearlyPerMonth)}/mo yearly`,
      usage: `32 of ${hobby.credits} workflow credits and about 32 of ${hobby.exportMinutes} export minutes`,
      note: "Prices from the live plan table.",
    },
    them: {
      plan: "Business",
      cost: "$69/mo or $41/mo yearly",
      usage:
        "A 60-minute source is over every per-video cap (2, 5 or 30 minutes by plan), so each source needs two uploads on Business, the only plan that takes a 30-minute file: 16 uploads at 3 credits each is 48 of 300 credits and 16 of 100 videos, and the clips Magic Clips returns per upload are not priced separately",
      note: "Per-video caps and 3 credits per video, per the Submagic pricing page.",
    },
  },
  test: {
    status: "scheduled",
    source: "A 20-minute single-speaker talking-head video owned by Crayo",
    plans: "Submagic Starter and Crayo Clipper, same file, same day, defaults first then one tuned run each",
    notes: [
      "Submagic Starter caps each video at 2 minutes, so the caption run uses the same 2-minute segment on both sides and the full-file run is recorded on whichever Submagic plan accepts it.",
      "Recorded per side: time to first clip, clips produced, clips judged usable by the rubric, credits spent, list-price dollars, export resolution and caption word errors on a fixed 60-second segment.",
    ],
  },
  billingNote:
    "Submagic prints three limits per plan: credits, a monthly video count and a per-video minute cap, and annual billing is shown only as a per-month figure. The pricing page does not state refund, cancellation or credit rollover terms, and its Starter card and comparison table disagree on included API minutes. Crayo credits reset monthly with no rollover, all sales are final, and cancelling is one click in Settings with the plan active to the end of the cycle.",
  faqs: [
    {
      question: "Is Crayo cheaper than Submagic for clipping?",
      answer: `Yes, for a clipper's month. One 60-minute podcast a week, eight clips each, is 32 clips: Crayo Hobby is ${money(hobby.monthly)} a month or ${money(hobby.yearlyTotal)} a year and covers that with 32 of ${hobby.credits} workflow credits and about 32 of its ${hobby.exportMinutes} export minutes. On Submagic the per-video cap decides the plan before the credits do: Starter at $19 takes 2 minutes per video, Pro at $39 takes 5, and only Business at $69 a month, or $41 billed annually, takes a 30-minute file, so a 60-minute source still needs two uploads there.`,
    },
    {
      question: "Can Submagic clip a 60-minute podcast?",
      answer: `Not in one upload, per its pricing page checked Oct 8, 2026. Submagic caps each video at 2 minutes on Starter, 5 on Pro and 30 on Business, and Magic Clips promises 20+ clips from an upload inside those caps. Crayo's AutoClip takes a YouTube or TikTok link or a file from 1 minute to 3 hours, returns 1 to 20 ranked clips of 15 to 120 seconds with a face-aware camera, and Find Viral Moments lists up to 50 ranked moments before any credit is spent. Longer editor exports draw on ${formatHours(hobby.exportMinutes)} of export a month on Hobby and ${formatHours(clipper.exportMinutes)} on Clipper.`,
    },
    {
      question: "Does Crayo have animated captions like Submagic?",
      answer:
        "Partly. Crayo ships 32 caption styles with keyword highlight across 56 spoken languages, and AutoClip applies them to every clip and keeps them off the speaker's face. Submagic's captions are its core product, with animated templates, emoji placement and a claimed 123 languages, and its styling library is deeper than Crayo's. If the caption look is the whole decision, Submagic wins that row. If captions are one layer on a clip that also needs reframing, zooms and cuts, Crayo does all of it on one plan.",
    },
    {
      question: "Does Crayo schedule posts like Submagic?",
      answer:
        "No. Crayo has no scheduler and no auto-posting; you download the 1080p export and post it yourself. Submagic has a social media scheduler, and if publishing from the editing tool is the deciding feature, Submagic wins that row. Crayo's View Tracker is not a scheduler either: it tracks Instagram, YouTube and TikTok creators, their posting cadence and their viral outliers, so you can see what is working before you clip the next source.",
    },
    {
      question: "Is there a free Submagic alternative?",
      answer: `Not Crayo, if free means a free plan. Crayo has no free tier and no trial; the entry plan is Hobby at ${money(hobby.monthly)} a month. Submagic's own comparison pages offer 3 free videos with no credit card, and third-party summaries report a watermark on those exports. What Crayo does give away is twelve browser tools at crayo.ai/features/free, including a video cutter, that need no account and never upload the file. For an ongoing free caption workflow, Submagic's free account or CapCut is the honest answer.`,
    },
    {
      question: "Submagic vs Opus Clip: where does Crayo fit?",
      answer:
        "Submagic is a caption tool that added clipping; Opus Clip is a clipper that meters by uploaded minute; Crayo is a clipping editor that meters by finished clip and opens every clip in a timeline. If you mostly caption short videos you already cut, compare Submagic and Crayo. If you mostly turn long recordings into clips, compare Opus Clip and Crayo, and note that Submagic Starter will not accept a source over 2 minutes. Crayo is the only one of the three without a free plan.",
    },
    {
      question: "How do Submagic credits work?",
      answer:
        "Submagic charges 3 credits per video on every plan as of Oct 8, 2026: Starter is 45 credits for 15 videos, Pro is 120 for 40 and Business is 300 for 100. On top of the credits, each plan caps the length of a single video at 2, 5 or 30 minutes. The pricing page does not say whether unused credits roll over, and it lists API minutes as 100 a month on every plan card but 10, 10 and 100 in its own comparison table. Crayo has one meter, 1 workflow credit per finished clip, plus export minutes, and credits reset monthly with no rollover.",
    },
    {
      question: "Can I move from Submagic to Crayo?",
      answer:
        "Yes, and there is nothing to migrate. Paste a YouTube or TikTok link or upload the file, pick a layout and intensity, and export at 1080p or open the clips in the editor. Your source files stay yours on both sides. Submagic's pricing page does not describe how to cancel, so check your account settings there before the next renewal date; Crayo cancels in one click from Settings and the plan stays active to the end of the cycle.",
    },
  ],
  changelog: [
    {
      date: "2026-10-09",
      text: "First published. Submagic prices checked Oct 8, 2026: Starter $19/mo or $12/mo billed annually with a 2-minute cap per video, Pro $39/mo or $23/mo with a 5-minute cap, Business $69/mo or $41/mo with 4K at 60fps.",
    },
    {
      date: "2026-10-25",
      text: `Crayo prices updated to the October 25, 2026 ladder: Hobby is ${crayoPlanTerms(hobby)}, with quarterly the default term at checkout. Verdicts and the corrections box re-checked at the new prices; competitor prices unchanged from the October 8 to 9 check.`,
    },
  ],
  relatedSpokes: ["crayo-vs-captions-ai", "crayo-vs-opus-clip", "crayo-vs-capcut"],
  featurePath: "/features/auto-clip",
  ctaHref: "https://crayo.ai/create/auto-clip",
  ctaLabel: "Clip a video now",
  targetQueries: [
    "crayo vs submagic",
    "submagic vs crayo",
    "submagic alternative",
    "submagic alternative free",
    "submagic pricing",
    "submagic vs opus",
    "submagic vs capcut",
  ],
};
